import { useCallback, useEffect, useState } from 'react';

export const RECENT_STORAGE_KEY = 'frm-ai.launcher.recent.v1';
const MAX_RECENT = 6;

function readStorage(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((x): x is string => typeof x === 'string').slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function writeStorage(ids: string[]): void {
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch {
    /* localStorage может быть отключён — игнорим */
  }
}

/**
 * LRU список id недавно открытых сервисов. Хранится в localStorage.
 * `bump(id)` — поднимает id наверх; max 6 элементов.
 *
 * Используется RecentStrip + Smart Hero (featured = recent[0] || firstLive).
 * См. lib/featured.ts (F2).
 */
export function useRecent() {
  const [ids, setIds] = useState<string[]>(() => readStorage());

  useEffect(() => {
    // Sync across tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === RECENT_STORAGE_KEY) setIds(readStorage());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const bump = useCallback((id: string) => {
    setIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, MAX_RECENT);
      writeStorage(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setIds([]);
    writeStorage([]);
  }, []);

  return { ids, bump, clear };
}
